/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FluidPreloader } from 'components/core/preloader';
import { PrimaryButton } from 'ui';
import { actions } from 'data';
import { sitesPath } from 'constants/urls';
import s from './styles.module.scss';

interface WeChatOAuthResponse {
  user_exists: boolean;
  unionid?: string;
  registration_token?: string;
  access?: string;
}

interface WeChatRegistrationData {
  unionid: string;
  registration_token: string;
}

const WeChatCallback: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleWeChatCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        // Validate required parameters
        if (!code) {
          throw new Error('Authorization code is missing');
        }

        if (!state) {
          throw new Error('State parameter is missing');
        }

        // Validate state parameter against stored state
        const storedState = localStorage.getItem('wechat_oauth_state');
        if (state !== storedState) {
          throw new Error('Invalid state parameter');
        }

        // Clear the stored state
        localStorage.removeItem('wechat_oauth_state');

        // Check for invite token in URL (if user was invited)
        const inviteToken = urlParams.get('invite_token');

        // Call the WeChat OAuth callback action
        const result = (await dispatch(
          actions.wechat.wechatOAuthCallback(code, state, inviteToken) as any,
        )) as WeChatOAuthResponse;

        console.log('WeChat authentication response:', result);

        if (result.user_exists) {
          // User exists, redirect to main application
          navigate(sitesPath, { replace: true });
        } else {
          // Preserve the full WeChat registration context for the new-account flow.
          localStorage.setItem(
            'wechat_registration_data',
            JSON.stringify({
              unionid: result.unionid || '',
              registration_token: result.registration_token || '',
            }),
          );
          // Store the registration token separately for the existing-account link flow.
          localStorage.setItem('wechat_registration_token', result.registration_token || '');

          // Redirect to account choice page
          navigate('/wechat/account-choice', { replace: true });
        }
      } catch (error) {
        console.error('WeChat OAuth callback error:', error);
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setError(errorMessage);
      }
    };

    handleWeChatCallback();
  }, [location, dispatch, navigate]);

  if (error) {
    return (
      <div className={s.sharedForm}>
        <div className={s.wechatCallbackContainer}>
          <h2 className={s.wechatCallbackErrorTitle}>{t('wechat_login_error') || 'WeChat Login Error'}</h2>
          <p className={s.wechatCallbackErrorText}>{error}</p>
          <PrimaryButton type="button" className={s.sharedFormSubmit} onClick={() => (window.location.href = '/')}>
            {t('back_to_login') || 'Back to Login'}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className={s.sharedForm}>
      <div className={s.wechatCallbackContainer}>
        <h2 className={s.wechatCallbackTitle}>{t('wechat_login_processing') || 'Processing WeChat Login...'}</h2>
        <FluidPreloader />
        <p className={s.wechatCallbackProcessingText}>
          {t('wechat_login_please_wait') || 'Please wait while we complete your login.'}
        </p>
      </div>
    </div>
  );
};

export default WeChatCallback;
